<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Calendario de Clases y Eventos</span>
            <v-btn color="primary" prepend-icon="mdi-plus" variant="flat" size="small"
              @click="abrirNuevo()">Nuevo Evento</v-btn>
          </v-card-title>
          <v-card-text>
            <div class="calendar">
              <FullCalendar ref="calendar" :options="calendarOptions">
                <template #eventContent="{ event, timeText }">
                  <div class="event-content d-flex flex-column" :title="event.title + '\n' + (event.extendedProps.descripcion || '')">
                    <div class="event-info">
                      <div><b>{{ timeText }}</b></div>
                      <div class="d-flex align-center" style="flex-wrap: wrap; gap: 2px;">
                        <span>
                          <b v-if="event.extendedProps.esClase">
                            Clase<template v-if="event.extendedProps.numero"> {{ event.extendedProps.numero }}</template> -
                          </b>
                          {{ event.title }}
                          <i v-if="event.extendedProps.duracion">({{ event.extendedProps.duracion }}m)</i>
                        </span>

                        <span v-if="event.extendedProps.circuito" class="badge-circuito">
                          Circuito
                        </span>
                      </div>
                    </div>

                    <div v-if="event.extendedProps.esClase && event.extendedProps.matriculaId"
                      class="event-actions mt-auto d-flex flex-wrap" style="gap: 2px;">
                      <v-btn
                        icon
                        density="compact"
                        style="width: 20px; height: 20px;"
                        variant="text"
                        color="indigo"
                        title="Ir al calendario del alumno (firmar / programar)"
                        @click.stop="irACalendarioAlumno(event.extendedProps.matriculaId)"
                      >
                        <v-icon size="14">mdi-magnify</v-icon>
                      </v-btn>
                    </div>

                    <div v-if="!event.extendedProps.esClase" class="event-actions mt-auto d-flex flex-wrap" style="gap: 2px;">
                      <v-btn
                        icon
                        density="compact"
                        style="width: 20px; height: 20px;"
                        variant="text"
                        @click.stop="abrirEditar({ event })"
                      >
                        <v-icon size="14">mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        density="compact"
                        style="width: 20px; height: 20px;"
                        variant="text"
                        @click.stop="confirmDelete(event.id)"
                      >
                        <v-icon size="14">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </div>
                </template>
              </FullCalendar>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <EventoDialog :dialog="dialog" :editedItem="editedEvento" form-title="Evento" @update:dialog="dialog = $event"
      @save="guardarEvento" @delete="eliminarEvento" />
      
    <v-dialog v-model="dialogDelete" width="auto" persistent>
      <v-card max-width="400" prepend-icon="mdi-delete" title="Eliminar Evento"
        text="¿Está seguro de eliminar este evento?">
        <template #actions>
          <v-spacer />
          <v-btn color="blue-darken-1" text @click="closeDelete">Cancelar</v-btn>
          <v-btn color="red-darken-1" text @click="deleteItemConfirm">Eliminar</v-btn>
        </template>
      </v-card>
    </v-dialog>
    
    <v-snackbar v-model="snackbar.show" :timeout="snackbar.timeout" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent, ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import FullCalendar from '@fullcalendar/vue3'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import esLocale from '@fullcalendar/core/locales/es'
import EventoDialog from '@/components/evento/EventoDialog.vue'

const parseBackendDate = (str) => {
  if (!str) return new Date()
  const d = new Date(str)
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
}

const toUTCString = (date) => date.toISOString()

export default defineComponent({
  components: { FullCalendar, EventoDialog },
  setup() {
    const store = useStore()
    const router = useRouter()
    const eventos = ref([])
    const calendarRef = ref(null)
    const dialog = ref(false)
    const editedEvento = ref({})
    const dialogDelete = ref(false)
    const idToDelete = ref(null)
    const snackbar = ref({ show: false, message: '', color: 'success', timeout: 3000 })
    const mostrarSnackbar = (msg, color = 'success') => {
      snackbar.value = { show: true, message: msg, color, timeout: 3000 }
    }

    const calendarOptions = computed(() => ({
      locale: esLocale,
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: 'timeGridWeek',
      timeZone: 'America/Lima',
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay',
      },
      allDaySlot: false,
      height: '80vh',
      expandRows: true,
      slotDuration: '00:15:00',
      slotLabelInterval: '01:00',
      slotMinTime: '05:00:00',
      slotMaxTime: '19:00:00',
      slotLabelFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      },
      eventTimeFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      },

      events: eventos.value,
      dateClick(info) {
        abrirNuevo(info.dateStr)
      },

      eventClick(info) {
        if (info.event.extendedProps.esClase) return
        if (!info.event.id) return
        abrirEditar(info)
      },
      datesSet(info) {
        cargarDatos({ start: info.startStr, end: info.endStr })
      },
    }))

    const cargarDatos = async ({ start, end } = {}) => {
      try {
        const params = {}
        if (start && end) {
          params.fecha_inicio = start
          params.fecha_fin = end
        }
        const todas = await store.dispatch('alumno/fetchProgramaciones', params)
        const progs = todas.filter((prog) => !prog.cancelacion).map((prog) => {
          const alumno = prog.alumno || prog.matricula?.alumno || {}
          const nombre = `${alumno.nombres || ''} ${alumno.apellido_paterno || ''} ${alumno.apellido_materno || ''}`.trim() || 'Alumno'
          const start = parseBackendDate(prog.fecha_hora_inicio)
          const end = parseBackendDate(prog.fecha_hora_fin)
          const descr = typeof prog.clase === 'string' ? prog.clase : (prog.clase?.descripcion || '')
          const numero = prog.numero_clase || prog.clase?.numero || ''
          return {
            title: nombre,
            descripcion: descr,
            numero,
            esClase: true,
            matriculaId: prog.matricula_id ?? prog.matricula?.id ?? null,
            start,
            end,
            originalStart: toUTCString(start),
            duracion: (end.getTime() - start.getTime()) / 60000,
            classNames: ['calendar-event'],
            textColor: '#000',
            circuito: !!prog.circuito,
          }
        })

        await store.dispatch('evento/fetchEventos')
        const raw = store.getters['evento/eventos']
        const evs = raw.map((ev) => ({
          id: ev.id,
          title: ev.titulo,
          descripcion: ev.descripcion,
          start: parseBackendDate(ev.fecha_inicio),
          end: ev.fecha_fin ? parseBackendDate(ev.fecha_fin) : null,
          classNames: ['other-event'],
          textColor: '#fff',
        }))

        eventos.value = [...progs, ...evs]
      } catch (e) {
        console.error('Error cargando calendario', e)
      }
    }

    const irACalendarioAlumno = (matriculaId) => {
      if (!matriculaId) return
      router.push({
        name: 'programarClases',
        params: { matriculaId },
      })
    }

    const abrirNuevo = (dateStr = null) => {
      editedEvento.value = {
        titulo: '',
        descripcion: '',
        fecha_inicio: dateStr ? `${dateStr}T08:00` : '',
        fecha_fin: dateStr ? `${dateStr}T09:00` : '',
      }
      dialog.value = true
    }

    const abrirEditar = (info) => {
      const ev = info.event
      editedEvento.value = {
        id: ev.id,
        titulo: ev.title,
        descripcion: ev.extendedProps.descripcion,
        fecha_inicio: ev.start ? ev.start.toISOString().slice(0, 16) : '',
        fecha_fin: ev.end ? ev.end.toISOString().slice(0, 16) : '',
      }
      dialog.value = true
    }

    const guardarEvento = async (ev) => {
      try {
        if (ev.id) await store.dispatch('evento/updateEvento', ev)
        else await store.dispatch('evento/guardarEvento', ev)
        dialog.value = false
        await cargarDatos()
        mostrarSnackbar('Evento guardado')
      } catch (e) {
        console.error('Error guardando evento', e)
        const msg = e?.response?.data?.message || 'Error guardando evento'
        mostrarSnackbar(msg, 'error')
      }
    }

    const eliminarEvento = async (id) => {
      try {
        await store.dispatch('evento/deleteEvento', id)
        dialog.value = false
        await cargarDatos()
        mostrarSnackbar('Evento eliminado')
      } catch (e) {
        console.error('Error eliminando evento', e)
        const msg = e?.response?.data?.message || 'Error eliminando evento'
        mostrarSnackbar(msg, 'error')
      }
    }

    const confirmDelete = (id) => {
      idToDelete.value = id
      dialogDelete.value = true
    }

    const deleteItemConfirm = async () => {
      if (!idToDelete.value) return
      await eliminarEvento(idToDelete.value)
      dialogDelete.value = false
      idToDelete.value = null
    }

    const closeDelete = () => {
      dialogDelete.value = false
      idToDelete.value = null
    }

    onMounted(() => {
      const api = calendarRef.value?.getApi()
      if (api?.view) {
        cargarDatos({
          start: api.view.activeStart.toISOString(),
          end: api.view.activeEnd.toISOString(),
        })
      }
    })

    return {
      calendarRef,
      calendarOptions,
      dialog,
      editedEvento,
      abrirNuevo,
      irACalendarioAlumno,
      guardarEvento,
      eliminarEvento,
      confirmDelete,
      deleteItemConfirm,
      closeDelete,
      abrirEditar,
      snackbar,
      mostrarSnackbar,
      dialogDelete,
    }
  },
})
</script>

<style scoped>
.calendar {
  max-width: 100%;
  margin: 0 auto;
}

.event-content {
  width: 100%;
  height: 100%;
  padding: 1px 2px; /* Reducido un poquito para ganar espacio vertical */
  overflow: hidden;
}

.event-info {
  font-size: 0.65rem; /* Ajustado para que quepa más texto en 45m */
  line-height: 1.05;
  white-space: normal !important;
  word-break: break-word;
}

.event-actions {
  margin-top: 1px;
}

/* Estilo exacto para nuestra etiqueta Circuito evitando el tamaño por defecto de v-chip */
.badge-circuito {
  background-color: #d32f2f; /* color red-darken-2 de Vuetify */
  color: white;
  font-size: 0.55rem;
  padding: 1px 4px;
  border-radius: 3px;
  display: inline-block;
  line-height: 1;
  font-weight: 500;
  margin-top: 2px;
}

:deep(.fc-event-main) {
  padding: 0 !important;
}

:deep(.calendar-event) {
  background-color: #f2cd14 !important;
  border-color: #f2cd14 !important;
  color: #000 !important;
}

:deep(.other-event) {
  background-color: #424242 !important;
  border-color: #424242 !important;
  color: #fff !important;
}
</style>